import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../api";

/**
 * Hook for the EEGAnalysis component.
 * @param {Object} selectedSessionForAnalysis - The selected session for analysis.
 * @param {Function} refreshPatientDetails - The function to refresh the patient details.
 * @param {string} displayMetric - The display metric ('amplitude' or 'percentage').
 * @returns {
 *  eegFile: File, // The EEG file
 *  isLoadingUpload: boolean, // Whether the upload is loading
 *  uploadError: string, // The upload error
 *  analysisDeviations: Array, // The analysis deviations
 *  isLoadingAnalysis: boolean, // Whether the analysis is loading
 *  analysisError: string, // The analysis error
 *  heatmapRawData: Object, // The heatmap raw data
 *  isLoadingHeatmap: boolean, // Whether the heatmap is loading
 *  heatmapError: string, // The heatmap error
 *  deviationDisplayThreshold: number, // The deviation display threshold
 *  machineTypeForAnalysis: string, // The machine type for analysis
 *  docxUploaded: boolean, // Whether the EEG DOCX is available
 *  handleFileChange: function, // The function to change the file
 *  handleMachineTypeChange: function, // The function to change the machine type
 *  handleStartAnalysis: function, // The function to start the analysis
 *  handleFileUpload: function, // The function to upload the file
 *  setDeviationDisplayThreshold: function, // The function to set the deviation display threshold
 *  setAnalysisDeviations: function, // The function to set the analysis deviations
 * } - The hook object.
 */
export const useEEGAnalysis = (
  selectedSessionForAnalysis,
  refreshPatientDetails,
  displayMetric = 'amplitude'
) => {
  const [eegFile, setEegFile] = useState(null);
  const [isLoadingUpload, setIsLoadingUpload] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [analysisDeviations, setAnalysisDeviations] = useState([]);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [heatmapRawData, setHeatmapRawData] = useState(null);
  const [isLoadingHeatmap, setIsLoadingHeatmap] = useState(false);
  const [heatmapError, setHeatmapError] = useState(null);
  const [deviationDisplayThreshold, setDeviationDisplayThreshold] =
    useState(10);
  const [machineTypeForAnalysis, setMachineTypeForAnalysis] =
    useState("fundacja_orchidea");
  const [docxUploaded, setDocxUploaded] = useState(false);
  const [normativeProfiles, setNormativeProfiles] = useState([]);
  const [selectedNormativeProfileName, setSelectedNormativeProfileName] =
    useState(null);
  const [suggestedInterpretationThemes, setSuggestedInterpretationThemes] =
    useState([]);
  const [sessionRatios, setSessionRatios] = useState([]);
  const [isLoadingRatios, setIsLoadingRatios] = useState(false);
  const [ratioError, setRatioError] = useState(null);
  const [requireFullCoverage, setRequireFullCoverage] = useState(false);
  const previousSettingsRef = useRef({
    normativeProfile: null,
    machineType: null,
  });

  // Fetch available normative profiles once
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const res = await api.getNormativeProfiles();
        const profiles = Array.isArray(res?.data) ? res.data : [];
        setNormativeProfiles(profiles);
      } catch (e) {
        console.error("Failed to fetch normative profiles", e);
      }
    };
    fetchProfiles();
  }, []);

  const fetchAndSetSessionRatios = useCallback(async (sessionId) => {
    if (!sessionId) {
      setRatioError("No valid session ID provided");
      setSessionRatios([]);
      return;
    }

    setIsLoadingRatios(true);
    setRatioError(null);
    try {
      const response = await api.getSessionRatios(sessionId);
      const ratios = Array.isArray(response?.data) ? response.data : [];
      setSessionRatios(ratios);
    } catch (err) {
      console.error("Error fetching session ratios:", err);
      setRatioError(
        err.response?.data?.detail || "Failed to fetch ratio comparisons."
      );
      setSessionRatios([]);
    } finally {
      setIsLoadingRatios(false);
    }
  }, []);

  useEffect(() => {
    if (selectedSessionForAnalysis?.id) {
      const checkDocxStatus = async () => {
        try {
          const response = await api.getSessionDetails(
            selectedSessionForAnalysis.id
          );
          setMachineTypeForAnalysis(response.data.machine_type);
          setSelectedNormativeProfileName(response.data.normative_profile_name);
          setAnalysisDeviations(response.data.deviations);
          const hasDocx = response.data.deviations.length > 0;
          setDocxUploaded(hasDocx);

          if (hasDocx) {
            if (Array.isArray(response.data?.ratios)) {
              setSessionRatios(response.data.ratios);
              setRatioError(null);
            } else {
              await fetchAndSetSessionRatios(selectedSessionForAnalysis.id);
            }
          } else {
            setSessionRatios([]);
            setRatioError(null);
          }
        } catch (err) {
          console.error("Error checking DOCX status:", err);
          setDocxUploaded(false);
          setSessionRatios([]);
          setRatioError(
            err.response?.data?.detail || "Failed to load session ratios."
          );
        }
      };
      checkDocxStatus();
    }
  }, [selectedSessionForAnalysis, fetchAndSetSessionRatios]);

  useEffect(() => {
    setEegFile(null);
    setUploadError(null);
    setAnalysisDeviations([]);
    setAnalysisError(null);
    setHeatmapRawData(null);
    setHeatmapError(null);
    setSessionRatios([]);
    setRatioError(null);
  }, [selectedSessionForAnalysis]);

  const getSuggestedITs = useCallback(async () => {
    if (!selectedSessionForAnalysis?.id) {
      setSuggestedInterpretationThemes([]);
      return;
    }
    try {
      // Pass min_coverage=100 when requireFullCoverage is true
      const minCoverage = requireFullCoverage ? 100 : null;
      const response = await api.getSessionInterpretationThemes(
        selectedSessionForAnalysis.id,
        minCoverage,
        displayMetric
      );
      const themes = response.data.themes.map((it, i) => {
        // Use all_combinations to get the channels/bands found in session data
        const allCombinations =
          it.all_combinations || it.exceeding_combinations;
        const channels = new Set();
        const bands = new Set();

        allCombinations.forEach((combination) => {
          // Normalize channel names to uppercase for case-insensitive comparison
          channels.add(combination.channel?.toUpperCase());
          bands.add(combination.frequency_band);
        });

        // Also create sets from theme definition (theoretical channels/bands)
        // Normalize to uppercase to match session data channels
        const themeDefinedChannels = new Set(
          (it.theme_involved_channels || []).map(ch => ch?.toUpperCase())
        );
        const themeDefinedBands = new Set(it.theme_involved_bands || []);

        return {
          ...it,
          channels: channels, // Channels found in session data
          bands: bands, // Bands found in session data
          themeDefinedChannels: themeDefinedChannels, // All channels from theme definition
          themeDefinedBands: themeDefinedBands, // All bands from theme definition
          color: `hsl(${i * 30}, 100%, 65%, 0.3)`,
          colorHover: `hsl(${i * 30}, 100%, 65%)`,
        };
      });
      setSuggestedInterpretationThemes(themes);
    } catch (err) {
      console.error(
        "Error fetching session suggested interpretation themes:",
        err
      );
      setSuggestedInterpretationThemes([]);
    }
  }, [selectedSessionForAnalysis, requireFullCoverage, displayMetric]);

  useEffect(() => {
    if (selectedSessionForAnalysis?.id) {
      getSuggestedITs();
    }
  }, [selectedSessionForAnalysis, getSuggestedITs]);

  const handleFileChange = (event) => {
    setEegFile(event.target.files[0]);
    setUploadError(null);
  };

  const handleMachineTypeChange = (e) => {
    setMachineTypeForAnalysis(e.target.value);
  };

  const fetchAndSetHeatmapData = useCallback(
    async (sessionId) => {
      if (!sessionId) {
        setHeatmapError("No valid session ID provided");
        return;
      }

      setIsLoadingHeatmap(true);
      setHeatmapError(null);
      try {
        const response = await api.getSessionHeatmap(
          sessionId,
          selectedNormativeProfileName,
          machineTypeForAnalysis
        );

        // Add defensive check for response data
        if (response && response.data) {
          setHeatmapRawData(response.data);
        } else {
          throw new Error("Invalid response data structure");
        }
      } catch (err) {
        console.error("Error fetching heatmap data:", err);
        setHeatmapError(
          err.response?.data?.detail || "Failed to fetch heatmap data."
        );
        setHeatmapRawData(null); // Reset to prevent rendering with bad data
      } finally {
        setIsLoadingHeatmap(false);
      }
    },
    [machineTypeForAnalysis, selectedNormativeProfileName]
  );

  const handleStartAnalysis = useCallback(async () => {
    if (!selectedSessionForAnalysis?.id) {
      setAnalysisError("No session selected for analysis.");
      return;
    }

    const sessionId = selectedSessionForAnalysis.id;
    setIsLoadingAnalysis(true);
    setAnalysisError(null);

    try {
      const response = await api.analyzeEEGData(
        sessionId,
        selectedNormativeProfileName,
        machineTypeForAnalysis
      );

      if (!response || response.data === undefined) {
        throw new Error("Invalid response data structure");
      }

      const payload = response.data;
      const deviations = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.deviations)
          ? payload.deviations
          : [];

      setAnalysisDeviations(deviations);
      setDocxUploaded(true);

      if (Array.isArray(payload?.ratios)) {
        setSessionRatios(payload.ratios);
        setRatioError(null);
      } else {
        await fetchAndSetSessionRatios(sessionId);
      }

      await fetchAndSetHeatmapData(sessionId);
      await getSuggestedITs();
    } catch (err) {
      console.error("Error analyzing qEEG data:", err);
      setAnalysisError(
        err.response?.data?.detail || "Failed to analyze qEEG data."
      );
    } finally {
      setIsLoadingAnalysis(false);
    }
  }, [
    selectedSessionForAnalysis,
    selectedNormativeProfileName,
    machineTypeForAnalysis,
    fetchAndSetHeatmapData,
    fetchAndSetSessionRatios,
    getSuggestedITs,
  ]);

  const handleFileUpload = async () => {
    if (!eegFile) {
      setUploadError("Please select a file to upload.");
      return;
    }

    if (!selectedSessionForAnalysis?.id) {
      setUploadError("No session selected. Please select a session first.");
      return;
    }

    setIsLoadingUpload(true);
    setUploadError(null);
    const formData = new FormData();
    formData.append("file", eegFile);

    try {
      const response = await api.uploadEEGDocx(
        selectedSessionForAnalysis.id,
        formData,
        true,
        selectedNormativeProfileName,
        machineTypeForAnalysis
      );
      setEegFile(null);
      const data = response?.data;
      if (data && data.deviations) {
        setAnalysisDeviations(
          Array.isArray(data.deviations) ? data.deviations : []
        );
      } else {
        setAnalysisDeviations([]);
      }
      if (Array.isArray(data?.ratios)) {
        setSessionRatios(data.ratios);
        setRatioError(null);
      } else {
        await fetchAndSetSessionRatios(selectedSessionForAnalysis.id);
      }
      setDocxUploaded(true);
      // Fetch heatmap immediately after upload auto-analysis
      await fetchAndSetHeatmapData(selectedSessionForAnalysis.id);
      await getSuggestedITs();
    } catch (err) {
      console.error("Error uploading EEG file:", err);
      setUploadError(
        err.response?.data?.detail || "Failed to upload EEG file."
      );
    } finally {
      setIsLoadingUpload(false);
    }
  };

  // Recalculate analysis when normative profile changes
  useEffect(() => {
    if (!selectedSessionForAnalysis?.id || !selectedNormativeProfileName) {
      previousSettingsRef.current = {
        normativeProfile: selectedNormativeProfileName,
        machineType: machineTypeForAnalysis,
      };
      return;
    }

    if (!docxUploaded) {
      previousSettingsRef.current = {
        normativeProfile: selectedNormativeProfileName,
        machineType: machineTypeForAnalysis,
      };
      return;
    }

    const { normativeProfile, machineType } = previousSettingsRef.current;
    const normChanged =
      normativeProfile !== null &&
      normativeProfile !== selectedNormativeProfileName;
    const machineChanged =
      machineType !== null && machineType !== machineTypeForAnalysis;

    if (normChanged || machineChanged) {
      handleStartAnalysis();
    }

    previousSettingsRef.current = {
      normativeProfile: selectedNormativeProfileName,
      machineType: machineTypeForAnalysis,
    };
  }, [
    selectedSessionForAnalysis,
    selectedNormativeProfileName,
    machineTypeForAnalysis,
    docxUploaded,
    handleStartAnalysis,
  ]);

  return {
    eegFile,
    isLoadingUpload,
    uploadError,
    analysisDeviations,
    isLoadingAnalysis,
    analysisError,
    heatmapRawData,
    isLoadingHeatmap,
    heatmapError,
    deviationDisplayThreshold,
    machineTypeForAnalysis,
    docxUploaded,
    normativeProfiles,
    selectedNormativeProfileName,
    suggestedInterpretationThemes,
    sessionRatios,
    isLoadingRatios,
    ratioError,
    setSelectedNormativeProfileName,
    handleFileChange,
    handleMachineTypeChange,
    handleStartAnalysis,
    handleFileUpload,
    setDeviationDisplayThreshold,
    setAnalysisDeviations,
    requireFullCoverage,
    setRequireFullCoverage,
  };
};
